import { useCallback, useState } from "react";

/**
 * The viewer's theme choice. "light" is the default; "system" follows the
 * operating system. Stored per browser in localStorage. index.html applies
 * the same rule before first paint, so keep the two in step.
 */
export type ThemeMode = "light" | "dark" | "system";

const KEY = "mia.theme";

function read(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "dark" || v === "system") return v;
  } catch {
    /* storage blocked: fall back to the default */
  }
  return "light";
}

function apply(mode: ThemeMode) {
  const el = document.documentElement;
  if (mode === "system") el.removeAttribute("data-theme");
  else el.setAttribute("data-theme", mode);
}

export function useThemeMode(): [ThemeMode, (m: ThemeMode) => void] {
  const [mode, setMode] = useState<ThemeMode>(read);
  const set = useCallback((m: ThemeMode) => {
    setMode(m);
    apply(m);
    try {
      localStorage.setItem(KEY, m);
    } catch {
      /* not remembered, still applied for this page */
    }
  }, []);
  return [mode, set];
}
